import { getFiscalYearRange } from '@packages/domain/utils/getFiscalYearRange/getFiscalYearRange';
import { Button } from 'components/ui/Button/Button';
import { DatePicker } from 'components/ui/DatePicker/DatePicker';
import { Heading } from 'components/ui/Heading/Heading';
import { TextInput } from 'components/ui/TextInput/TextInput';
import { useLRSContext } from 'contexts/LRS';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Back } from '../../components/Back/Back';
import './PreviousTransactions.scss';

const DEFAULT_TRANSACTION = { date: '', amount: '', adBranch: '' };

export const PreviousTransactions = () => {
  const { i18n, values, onNext, onBack, previousTransactions } =
    useLRSContext();
  const defaultTransactions = [DEFAULT_TRANSACTION];
  const [showForm, setShowForm] = useState(!!previousTransactions.length);
  const [transactions, setTransactions] = useState(defaultTransactions);
  const fiscalYearRange = getFiscalYearRange();

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want it to run only when values change
  useEffect(() => {
    const { transactions: savedTransactions } = values;
    if (savedTransactions?.length > 0) {
      setTransactions(savedTransactions);
      setShowForm(true);
    } else if (previousTransactions.length) {
      setShowForm(true);
    }
  }, [values]);

  function handleBack() {
    if (!showForm) {
      return onBack();
    }

    setTransactions(defaultTransactions);
    if (!!previousTransactions.length === false) {
      setShowForm(false);
    } else {
      onBack();
    }
  }

  function handleNo() {
    onNext('transactions', []);
  }

  function handleTransactionChange(elementName, updatedValue) {
    const [updatedTransactionIndex, updatedFieldName] = elementName.split('-');
    const transaction = transactions[updatedTransactionIndex];

    const updatedField = { [updatedFieldName]: updatedValue };
    const updatedTransaction = { ...transaction, ...updatedField };
    transactions[updatedTransactionIndex] = updatedTransaction;

    setTransactions(transactions);
  }

  function handleNext() {
    const filledTransactions = transactions.filter(
      transaction =>
        transaction.date || transaction.amount || transaction.adBranch,
    );

    onNext('transactions', filledTransactions);
  }

  function handleAddTransaction() {
    setTransactions([...transactions, DEFAULT_TRANSACTION]);
  }

  function Transaction({ transaction, index, disabled }) {
    return (
      <div className="LRSForm-transaction">
        <Heading as="h3" className="LRSForm-transaction-header" size="small">
          <span className="LRSForm-transaction-title">
            {i18n.t('checkout.LRS.previousTransfers.details.transaction')}
          </span>
          <hr className="LRSForm-transaction-line" />
        </Heading>
        <div className="LRSForm-transaction-fields">
          <div className="LRSForm-transaction-date">
            <DatePicker
              label={
                i18n.t('checkout.LRS.previousTransfers.details.date') as string
              }
              name={`${index}-date`}
              onChange={handleTransactionChange}
              value={transaction.date}
              disabled={disabled}
            />
          </div>
          <div className="LRSForm-transaction-amount">
            <TextInput
              name={`${index}-amount`}
              label={i18n.t('checkout.LRS.previousTransfers.details.amount')}
              onChange={handleTransactionChange}
              maxLength={20}
              hint={i18n.t('checkout.LRS.previousTransfers.details.amountHint')}
              value={transaction.amount}
              disabled={disabled}
            />
          </div>
          <div className="LRSForm-transaction-adbranch">
            <TextInput
              name={`${index}-adBranch`}
              label={i18n.t('checkout.LRS.previousTransfers.details.ADBranch')}
              onChange={handleTransactionChange}
              maxLength="50"
              value={transaction.adBranch}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    );
  }

  Transaction.propTypes = {
    disabled: PropTypes.bool,
    index: PropTypes.number,
    transaction: PropTypes.shape({
      date: PropTypes.string,
      amount: PropTypes.string,
      adBranch: PropTypes.string,
    }),
  };

  function TransactionsForm() {
    return (
      <div className="flex flex-col min-h-full">
        <div className="flex-1">
          <Back onClick={handleBack} className="marginBottom-md" />
          <Heading
            as="h2"
            size="large"
            className="fontWeight-semiBold"
            data-testid="lrsFiscalYearHeading"
          >
            {i18n.t('checkout.LRS.previousTransfers.details.title', {
              startYear: fiscalYearRange[0],
              endYear: fiscalYearRange[1],
            })}
          </Heading>
          <div className="LRSForm-transactions flex flex-col gap-4">
            {previousTransactions.map((transaction, index) => (
              <Transaction
                transaction={transaction}
                index={index}
                key={index}
                disabled
              />
            ))}
            {transactions.map((transaction, index) => {
              const tIndex = index + previousTransactions.length;
              return (
                <Transaction
                  transaction={transaction}
                  index={tIndex}
                  key={tIndex}
                />
              );
            })}
          </div>
          <Button
            onClick={handleAddTransaction}
            data-testid="addTransaction"
            className="marginBottom-lg mt-8"
            tone="secondary"
            size="full"
          >
            {i18n.t('checkout.LRS.previousTransfers.details.addTransaction')}
          </Button>
        </div>
        <div className="mt-auto pt-6 pb-4">
          <Button
            tone="primary"
            size="full"
            data-testid="next"
            onClick={handleNext}
          >
            {i18n.t('checkout.LRS.previousTransfers.details.next')}
          </Button>
        </div>
      </div>
    );
  }

  function TransactionsQuestionaire() {
    return (
      <div className="flex flex-col min-h-full">
        <div className="flex-1">
          <Back onClick={handleBack} className="marginBottom-md" />
          <Heading
            as="h2"
            size="large"
            className="fontWeight-semiBold"
            data-testid="lrsFiscalYearHeading"
          >
            {i18n.t('checkout.LRS.previousTransfers.question.title', {
              startYear: fiscalYearRange[0],
              endYear: fiscalYearRange[1],
            })}
          </Heading>
          <div className="marginTop-xxxxl">
            <Button
              tone="secondary"
              className="Button Button--block marginBottom-md"
              onClick={() => setShowForm(true)}
            >
              {i18n.t('checkout.LRS.previousTransfers.question.yes')}{' '}
            </Button>
            <Button tone="primary" size="full" onClick={handleNo}>
              {i18n.t('checkout.LRS.previousTransfers.question.no')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return showForm ? <TransactionsForm /> : <TransactionsQuestionaire />;
};
